import 'react-quill/dist/quill.snow.css';

import { PaperClipOutlined } from '@ant-design/icons';
import { message } from '@repo/ui';
import type { UploadFile } from 'antd';
import {
  Button, Card, Tooltip, Upload,
} from 'antd';
import { attachmentsSelector, contentSelector } from 'email/selectors/email';
import { useUploadImageMutation } from 'email/services/emailService';
import {
  setAttachments,
  setContent,
} from 'email/store/email';
import type { ReactElement } from 'react';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { useDispatch, useSelector } from 'react-redux';

import DynamicFieldsSelect from '../DynamicFieldsSelect/DynamicFieldsSelect';
import styles from './RichTextEditor.module.scss';

const AlignStyle = Quill.import('attributors/style/align');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['small', 'large'];

Quill.register(AlignStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);

export default function RichTextEditor() {
  const attachments = useSelector(attachmentsSelector);
  const content = useSelector(contentSelector);
  const [uploadImage] = useUploadImageMutation();
  const quillRef = useRef<ReactQuill | null>(null);
  const dispatch = useDispatch();

  const [savedRange, setSavedRange] = useState< ReactQuill.Range | null>(
    null,
  ); // State to save the cursor position (range)

  const setEmailContent = useCallback((value: string) => {
    dispatch(setContent(value));
  }, [dispatch]);

  const handleAttachmentUpload = useCallback(async (options: any) => {
    const { file, onSuccess, onError } = options;

    const formData = new FormData();
    formData.append('asset', file);

    try {
      const response = await uploadImage(formData).unwrap();
      const fileUrl = response.url;
      const filename = file.name;
      const blobId = response.id;

      const transformedResponse = {
        ...response,
        blob_id: response.id,
      };
      delete transformedResponse.id;

      const newAttachment: UploadFile = {
        uid: file.uid,
        name: filename,
        url: fileUrl,
        status: 'done',
        response: transformedResponse,
      };

      dispatch(setAttachments([...attachments, newAttachment]));
      onSuccess({ url: fileUrl, filename, blob_id: blobId });

      message.success(`${filename} uploaded successfully.`);
    } catch (error: any) {
      onError(error);

      if (error?.data?.message) {
        message.error(error?.data?.message);
      } else {
        message.error('Failed to upload attachment.');
      }
    }
  }, [attachments, dispatch, uploadImage]);

  const handleAttachmentRemove = useCallback((file: UploadFile) => {
    const updatedAttachments = attachments.filter((attachment) => attachment.uid !== file.uid);
    dispatch(setAttachments(updatedAttachments));
  }, [attachments, dispatch]);

  const handleImageUpload = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append('asset', file);

    try {
      const response = await uploadImage(formData).unwrap();
      const imageUrl = response.url;
      return imageUrl;
    } catch (error: any) {
      if (error?.data?.message) {
        message.error(error?.data?.message);
      } else {
        message.error('Failed to upload image.');
      }
      return null;
    }
  }, [uploadImage]);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files ? input.files[0] : null;
      if (file) {
        const imageUrl = await handleImageUpload(file);
        if (imageUrl && quillRef.current) {
          const quillEditor = quillRef.current.getEditor();
          const range = quillEditor.getSelection();
          if (range) {
            quillEditor.insertEmbed(range.index, 'image', imageUrl);
          }
        }
      }
    };
  }, [handleImageUpload]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ font: [] }, { size: ['small', false, 'large'] }],
        [{ header: '1' }, { header: '2' }, { list: 'ordered' }, { list: 'bullet' }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ align: [] }],
        [{ color: [] }, { background: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler,
      },
    },
  }), [imageHandler]);

  const formats = [
    'font', 'size',
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'align', 'color', 'background',
    'link', 'image',
  ];

  // Save the current cursor position before opening the dropdown
  const saveCursorPosition = () => {
    if (quillRef.current) {
      const quillEditor = quillRef.current.getEditor();
      const range = quillEditor.getSelection();
      if (range) {
        setSavedRange(range);
      }
    }
  };

  const handleFieldInsert = (field: string) => {
    if (quillRef.current) {
      const quillEditor = quillRef.current.getEditor();

      // Restore the saved range (cursor position)
      if (savedRange) {
        quillEditor.setSelection(savedRange.index, savedRange.length);
      }

      const insertText = `@{${field}}`;
      const range = quillEditor.getSelection();
      if (range) {
        quillEditor.insertText(range.index, insertText);
      } else {
        const position = quillEditor.getLength(); // If no range, insert at the end
        quillEditor.insertText(position, insertText);
      }

      quillEditor.focus();
    }
  };

  const renderUploadItem = (originNode: ReactElement, file: UploadFile) => (
    <Tooltip title={file.name}>
      {originNode}
    </Tooltip>
  );

  return (
    <Card
      className={styles.container}
      classNames={{ body: styles.cardBody }}
      actions={[
        <div className={styles.dropdownContainer}>
          <Upload
            customRequest={handleAttachmentUpload}
            fileList={attachments}
            itemRender={renderUploadItem}
            onRemove={handleAttachmentRemove}
            showUploadList={{
              showRemoveIcon: true,
              showPreviewIcon: false,
            }}
          >
            <Button icon={<PaperClipOutlined />} style={{ marginRight: 12 }}>
              Add attachment
            </Button>
          </Upload>
          <DynamicFieldsSelect
            onDropdownVisibleChange={(open: boolean) => {
              if (open) saveCursorPosition();
            }}
            onChange={handleFieldInsert}
          />
        </div>,
      ]}
    >
      <div className={styles.richTextEditor}>
        <ReactQuill
          ref={quillRef}
          value={content}
          onChange={setEmailContent}
          modules={modules}
          formats={formats}
          theme="snow"
        />
      </div>
    </Card>
  );
}
